import {
    AnyObject,
    APIFinalArgs,
    APIParams,
    BaseFeatureArgs,
    InitialQueryState,
    QueryManagerOptions,
    QueryResponse,
    SkipOrArgs,
} from '@common/types/queryState';
import { mapQueryStateToApiParams } from '@common/utils/query/mapQueryStateToApiParams';
import { skipToken } from '@reduxjs/toolkit/query';
import {
    BaseQueryFn,
    TypedUseQueryHookResult,
} from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useQueryManager } from './useQueryManager';

export type FeatureQueryHookSignature<
    Response,
    Args extends BaseFeatureArgs,
> = (
    args: SkipOrArgs<APIFinalArgs<Args>>,
    options?: AnyObject,
) => TypedUseQueryHookResult<
    | QueryResponse<Response>
    | QueryResponse<Partial<Response>>
    | Response
    | Partial<Response>,
    SkipOrArgs<APIFinalArgs<Args>>,
    BaseQueryFn
>;

const isQueryResponse = <T>(
    result:
        | QueryResponse<T>
        | QueryResponse<Partial<T>>
        | T
        | Partial<T>
        | undefined
        | null,
): result is QueryResponse<T> => {
    return !!(
        result &&
        typeof result === 'object' &&
        'data' in result &&
        Array.isArray((result as QueryResponse<Partial<T>>).data)
    );
};

export const useFeatureQuery = <Response, Args extends BaseFeatureArgs>(
    initialArgs: InitialQueryState = {},
    useRTKHook: FeatureQueryHookSignature<Response, Args>,
    featureArgs: SkipOrArgs<Args>,
    options?: QueryManagerOptions,
) => {
    const { queryState, ...controls } = useQueryManager(initialArgs, options);

    const queryArgs: SkipOrArgs<APIFinalArgs<Args>> = useMemo(() => {
        if (featureArgs === skipToken) return skipToken;

        const {
            fixedFilterGenerators: _,
            overrideFilterGenerator: __,
            ...serializableArgs
        } = featureArgs;

        const params: APIParams = mapQueryStateToApiParams(
            queryState,
            featureArgs,
        );

        return {
            ...serializableArgs,
            params,
        } as APIFinalArgs<Args>;
    }, [featureArgs, queryState]);

    const { data: result, ...queryResult } = useRTKHook(queryArgs, options);

    const normalizedData = useMemo((): Response[] => {
        if (!result) return [];

        if (isQueryResponse<Response>(result)) {
            return (result.data as Response[]) || [];
        }

        return [result as Response];
    }, [result]);

    const itemCount = isQueryResponse<Response>(result)
        ? result.totalCount
        : result
          ? 1
          : 0;

    const pageCount = queryState.pageSize
        ? Math.ceil(itemCount / queryState.pageSize)
        : 1;

    const facetFields = isQueryResponse<Response>(result)
        ? result.facetFields
        : [];

    return {
        data: normalizedData,
        facet: facetFields,
        itemCount,
        pageCount,
        ...queryResult,
        ...controls,
    };
};

export type UseFeatureQueryResult<
    TData,
    TArgs extends BaseFeatureArgs,
> = ReturnType<typeof useFeatureQuery<TData, TArgs>>;
